/**
 * ThuysVers Subscription Cart Modal – Guest (sessionStorage)
 * Logic migrated from sections/thuysvers-subscription-cart-modal.liquid (logged-in) and
 * snippets/tv-subscription-cart-item-kind.liquid so guest behaviour matches 1:1.
 * - Item kind (meal/soup/dessert): same rules as tv-subscription-cart-item-kind.liquid.
 * - Counts, overflow, eenmalig, add-order: same as Liquid section.
 * No DOM creation; structure is in Liquid; this file only updates placeholders.
 */
/** @typedef {{ properties?: Record<string, string> | Array<{ name?: string; key?: string; value?: string; val?: string }>; product?: { title?: string; type?: string; metafields?: { custom?: { product_type?: { value?: unknown } | unknown } }; featured_image?: { url?: string; src?: string } }; product_type?: string; variant?: { title?: string; image?: { url?: string; src?: string } }; image?: { url?: string; src?: string } | string; featured_image?: { url?: string; src?: string }; title?: string; quantity?: number; final_price?: number; original_price?: number }} TvGuestCartItem */
/** @typedef {{ profiles?: Array<{ profileKey?: string; displayName?: string; mealsPerWeek?: number; portionSize?: { size?: string; unit?: string } }>; addOns?: Array<{ addonType?: string; quantity?: number; options?: { size?: string } }> }} TvGuestPayloadData */
/** @typedef {{ meal_label?: string; soup_label?: string; dessert_label?: string; default_profile_name?: string; [key: string]: unknown }} TvGuestSettingsData */
(function () {
  // SessionStorage key where guest survey payload (profiles, addOns) is stored; same as ecomm recommendations
  const STORAGE_KEY = 'recommendations';
  // Cart line item property name that holds the profile key (e.g. "p1", "p2") for subscription assignment
  const PROFILE_KEY_PROP = '_profile_key';

  /**
   * Toggle the hidden attribute on an element so we can show/hide sections and cards without removing from DOM.
   * @param {Element} el
   * @param {boolean} value
   */
  function setHidden(el, value) {
    if (el && 'hidden' in el) el.hidden = value;
  }

  /** Cart item properties can be object or (in some APIs) array of { name, value }. Return trimmed value.
   * @param {TvGuestCartItem} item
   * @param {string} key
   * @returns {string}
   */
  function getItemProperty(item, key) {
    const p = item?.properties;
    if (!p) return '';
    // Shopify cart JSON often has properties as plain object
    if (typeof p === 'object' && !Array.isArray(p) && p[key] != null) return String(p[key]).trim();
    // Some cart APIs return properties as array of { name, value } or { key, val }
    if (Array.isArray(p)) {
      const entry = p.find((e) => e && (e.name === key || e.key === key));
      return entry ? String(entry.value || entry.val || '').trim() : '';
    }
    return '';
  }

  /**
   * Return the profile key for this line item (which person this meal/soup/dessert is assigned to).
   * @param {TvGuestCartItem} item
   * @returns {string}
   */
  function getItemProfileKey(item) {
    return getItemProperty(item, PROFILE_KEY_PROP) || getItemProperty(item, 'profile_key') || '';
  }

  /**
   * Item kind: matches snippets/tv-subscription-cart-item-kind.liquid.
   * - soup: product_type metafield JSON contains "extras" AND product.type is 'soep' or 'soup'
   *   (when metafield missing in cart JSON, fallback: type soep/soup → soup)
   * - dessert: product.type is 'nagerechten'
   * - else: meal
   * Uses both item.product.type and item.product_type (line-level in /cart.js) so desserts/soups are never classified as meals.
   * @param {TvGuestCartItem} item
   * @returns {'meal'|'soup'|'dessert'}
   */
  function getItemKind(item) {
    if (!item) return 'meal';
    // Prefer product.type; fallback to line-level product_type (e.g. from /cart.js response)
    const productType = (item.product && item.product.type != null && item.product.type !== '')
      ? item.product.type
      : (item.product_type != null && item.product_type !== '')
        ? item.product_type
        : '';
    const shopifyTypeDown = (productType || '').toLowerCase();
    const productTypeMeta = /** @type {{ value?: unknown } | null | undefined } */ (
      item.product?.metafields?.custom?.product_type
    );
    const metaValue = productTypeMeta && typeof productTypeMeta === 'object' && 'value' in productTypeMeta && productTypeMeta.value != null ? productTypeMeta.value : productTypeMeta;
    let productTypeMetaJson = '';
    if (metaValue != null) {
      if (typeof metaValue === 'string') productTypeMetaJson = metaValue.toLowerCase();
      else {
        try {
          productTypeMetaJson = JSON.stringify(metaValue).toLowerCase();
        } catch (_) {
          productTypeMetaJson = String(metaValue).toLowerCase();
        }
      }
    }
    // Soup: type soep/soup and (metafield contains "extras" or no metafield)
    const isSoupType = shopifyTypeDown === 'soep' || shopifyTypeDown === 'soup';
    if (isSoupType && (productTypeMetaJson.includes('"extras"') || !productTypeMetaJson)) return 'soup';
    // Dessert: Shopify product type "nagerechten" or "toetjes"
    if (shopifyTypeDown === 'nagerechten' || shopifyTypeDown === 'toetjes') return 'dessert';
    return 'meal';
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  // Format price: cart JSON uses cents; display as €X,XX
  function formatMoney(cents) {
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  /**
   * Decode HTML entities (e.g. &#39; → ') so titles display correctly when set via textContent.
   * @param {string} str
   * @returns {string}
   */
  function decodeHtmlEntities(str) {
    if (typeof str !== 'string' || !str) return str;
    const div = document.createElement('div');
    div.innerHTML = str.replace(/</g, '&lt;');
    return div.textContent || str;
  }

  /**
   * Get first available image URL from line item (image, variant.image, featured_image).
   * @param {TvGuestCartItem} item
   * @returns {string}
   */
  function getItemImageUrl(item) {
    const sources = [item?.image, item?.variant?.image, item?.featured_image, item?.product?.featured_image];
    for (const img of sources) {
      if (!img) continue;
      if (typeof img === 'string') return img;
      if (img.url) return img.url;
      if (img.src) return img.src;
    }
    return '';
  }

  /**
   * @param {TvGuestPayloadData} payload
   * @returns {{ profileKeys: string[]; soupExpected: number; soupSize: string; dessertExpected: number; profilesMap: Map<string, unknown>; addons: unknown[] }}
   */
  function getGuestSubscriptionData(payload) {
    /** @type {string[]} */
    const profileKeys = [];
    const profilesMap = new Map();
    let soupExpected = 0;
    let soupSize = '';
    let dessertExpected = 0;
    // addOns from survey payload: soup, dessert (and optionally size for soup)
    const addons = Array.isArray(payload?.addOns) ? payload.addOns : [];

    // Build ordered list of profile keys and map key -> profile (mealsPerWeek, displayName, portionSize)
    if (Array.isArray(payload?.profiles)) {
      payload.profiles.forEach((/** @type {{ profileKey?: string } } */ p) => {
        if (p?.profileKey) {
          profileKeys.push(String(p.profileKey).trim());
          profilesMap.set(String(p.profileKey).trim(), p);
        }
      });
    }

    // Sum expected soup and dessert from addOns; capture soup size (e.g. "0.5l") from first soup addon
    addons.forEach((/** @type {{ addonType?: string; quantity?: number; options?: { size?: string } } } */ addon) => {
      const type = (addon?.addonType || '').toLowerCase();
      if (type === 'soup') {
        soupExpected += addon.quantity || 0;
        if (!soupSize && addon.options?.size) soupSize = addon.options.size;
      } else if (type === 'dessert') {
        dessertExpected += addon.quantity || 0;
      }
    });

    return { profileKeys, soupExpected, soupSize, dessertExpected, profilesMap, addons };
  }

  /**
   * Fill one product card placeholder (img, title, price). Hides card if no item.
   * showCompare: for meal/eenmalig we show compare-at price when discounted; for soup/dessert usually false.
   * @param {Element} cardEl
   * @param {TvGuestCartItem | null} item
   * @param {boolean} showCompare
   */
  function setCardContent(cardEl, item, showCompare) {
    if (!cardEl) return;
    if (!item) {
      setHidden(cardEl, true);
      return;
    }
    setHidden(cardEl, false);
    const img = cardEl.querySelector('.tv-subscription-cart-modal__product-image');
    const mediaEl = cardEl.querySelector('.tv-subscription-cart-modal__product-media');
    const titleEl = cardEl.querySelector('.tv-subscription-cart-modal__product-title');
    const priceCurrent = cardEl.querySelector('.tv-subscription-cart-modal__product-price-current');
    const priceCompare = cardEl.querySelector('.tv-subscription-cart-modal__product-price-compare');

    const url = getItemImageUrl(item);
    if (img && img instanceof HTMLImageElement) {
      img.src = url || '';
      img.alt = decodeHtmlEntities((item.title || item.product?.title || '').trim() || '');
    }
    if (mediaEl) setHidden(mediaEl, !url);
    // Cart JSON can have title on line item or on item.product; decode entities (e.g. &#39;) for display
    const title = decodeHtmlEntities((item.title || item.product?.title || '').trim());
    if (titleEl) titleEl.textContent = '1 × ' + title;
    if (priceCurrent) priceCurrent.textContent = formatMoney(item.final_price || 0);

    const hasCompare = showCompare && (item.original_price || 0) > (item.final_price || 0);
    if (priceCompare) {
      priceCompare.textContent = hasCompare ? formatMoney(item.original_price || 0) : '';
      setHidden(priceCompare, !hasCompare);
    }
  }

  function renderGuestContent() {
    const root = document.getElementById('tv-subscription-cart-modal-guest-root');
    if (!root) return;

    const emptyEl = root.querySelector('[data-tv-guest-empty]');
    const contentEl = root.querySelector('[data-tv-guest-content]');

    // Cart JSON is injected by Liquid into a script#tv-subscription-cart-guest-cart (from section or cart drawer)
    const cartEl = document.getElementById('tv-subscription-cart-guest-cart');
    // Labels and default profile name from section settings (meal_label, soup_label, dessert_label, default_profile_name)
    const settingsEl = document.getElementById('tv-subscription-cart-guest-settings');
    /** @type {TvGuestCartItem[]} */
    let cartItems = [];
    /** @type {TvGuestSettingsData} */
    let settings = {};
    try {
      if (settingsEl?.textContent) settings = /** @type {TvGuestSettingsData} */ (JSON.parse(settingsEl.textContent.trim()));
    } catch (_) { }
    try {
      if (cartEl?.textContent) {
        const data = JSON.parse(cartEl.textContent.trim());
        cartItems = data.items || data.line_items || [];
      }
    } catch (_) { }

    // Guest subscription data: profiles + addOns from sessionStorage (key "recommendations"), set after survey submit or status check
    /** @type {TvGuestPayloadData | null} */
    let payload = null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) payload = JSON.parse(raw);
    } catch (_) { }

    const s = settings || {};
    const mealLabel = (s && s.meal_label) || 'Maaltijd';
    const soupLabel = (s && s.soup_label) || 'Soep';
    const soupsLabel = (s && s.soups_label) || 'Soepen';
    const dessertLabel = (s && s.dessert_label) || 'Dessert';
    const defaultProfileName = (s && s.default_profile_name) || 'Persoon ';

    // No subscription payload (no profiles) → show empty state, hide content
    if (!payload?.profiles?.length) {
      if (emptyEl) setHidden(emptyEl, false);
      if (contentEl) setHidden(contentEl, true);
      return;
    }

    if (emptyEl) setHidden(emptyEl, true);
    if (contentEl) setHidden(contentEl, false);

    const sub = getGuestSubscriptionData(payload);
    // Shopify cart items are newest-first; reverse to add-order (oldest first) so profile/soup/dessert slots get oldest N and overflow → eenmalig = last-added
    const cartItemsByAddOrder = [...cartItems].reverse();

    // Build "Jouw abonnement" summary lines: "DisplayName - N meals" per profile, then soup/dessert
    /** @type {string[]} */
    const profileLineTexts = [];
    sub.profileKeys.forEach((pk) => {
      const p = sub.profilesMap.get(pk);
      const expectedMeals = (p && typeof p === 'object' && 'mealsPerWeek' in p) ? /** @type {number} */ (/** @type {{ mealsPerWeek?: number } } */ (p).mealsPerWeek) : 0;
      let mealCount = 0;
      cartItems.forEach((item) => {
        if (getItemProfileKey(item) !== pk || getItemKind(item) !== 'meal') return;
        mealCount += item.quantity || 0;
      });
      const exp = expectedMeals > 0 ? expectedMeals : mealCount;
      const displayName = ((p && typeof p === 'object' && 'displayName' in p ? /** @type {{ displayName?: string } } */ (p).displayName : '') || defaultProfileName || pk).trim();
      const lineText = (displayName + ' - ' + exp + ' ' + (mealLabel || 'Maaltijd')).trim();
      if (exp > 0) profileLineTexts.push(lineText);
    });

    // Fill profile summary line placeholders (data-tv-guest-sub-profile-line)
    const profileSubLineEls = root.querySelectorAll('[data-tv-guest-sub-profile-line]');
    profileSubLineEls.forEach((el, i) => {
      const textEl = el.querySelector('[data-tv-guest-sub-line-text]');
      const text = profileLineTexts[i];
      if (i < profileLineTexts.length && text) {
        if (textEl) textEl.textContent = text;
        setHidden(el, false);
      } else {
        if (textEl) textEl.textContent = '';
        setHidden(el, true);
      }
    });

    // Fill soup summary line placeholder (data-tv-guest-sub-soup-line)
    const soupLineEl = root.querySelector('[data-tv-guest-sub-soup-line]');
    if (soupLineEl) {
      const textEl = soupLineEl.querySelector('[data-tv-guest-sub-line-text]');
      if (sub.soupExpected > 0) {
        if (textEl) textEl.textContent = sub.soupExpected + ' ' + (soupsLabel || soupLabel || 'Soep');
        setHidden(soupLineEl, false);
      } else {
        if (textEl) textEl.textContent = '';
        setHidden(soupLineEl, true);
      }
    }

    // Fill dessert summary line placeholder (data-tv-guest-sub-dessert-line)
    const dessertLineEl = root.querySelector('[data-tv-guest-sub-dessert-line]');
    if (dessertLineEl) {
      const textEl = dessertLineEl.querySelector('[data-tv-guest-sub-line-text]');
      if (sub.dessertExpected > 0) {
        if (textEl) textEl.textContent = sub.dessertExpected + ' ' + (dessertLabel || 'Dessert');
        setHidden(dessertLineEl, false);
      } else {
        if (textEl) textEl.textContent = '';
        setHidden(dessertLineEl, true);
      }
    }

    // Total item count for "Jouw weekmenu" header (e.g. "5")
    const weekmenuCountEl = root.querySelector('[data-tv-guest-weekmenu-count]');
    if (weekmenuCountEl) weekmenuCountEl.textContent = String(cartItems.reduce((/** @type {number} */ sum, /** @type {TvGuestCartItem} */ i) => sum + (i.quantity || 0), 0));

    // Count soup, dessert, and meals per profile in cart. Exclude oneoff items from soup/dessert counts (they go to Eenmalig).
    let soupInCart = 0;
    let dessertInCart = 0;
    let oneoffQty = 0;
    const mealCountByProfile = new Map();
    sub.profileKeys.forEach((pk) => mealCountByProfile.set(pk, 0));
    cartItems.forEach((/** @type {TvGuestCartItem} */ item) => {
      const kind = getItemKind(item);
      const pk = getItemProfileKey(item);
      if (pk === 'oneoff') {
        oneoffQty += item.quantity || 0;
        return;
      }
      if (kind === 'soup') soupInCart += item.quantity || 0;
      else if (kind === 'dessert') dessertInCart += item.quantity || 0;
      else if (pk) mealCountByProfile.set(pk, (mealCountByProfile.get(pk) || 0) + (item.quantity || 0));
    });

    // Overflow: meals/soup/dessert beyond subscription expected go to Eenmalig
    let mealOverflowQty = 0;
    sub.profileKeys.forEach((pk) => {
      const prof = sub.profilesMap.get(pk);
      const expected = (prof && typeof prof === 'object' && 'mealsPerWeek' in prof) ? /** @type {number} */ (/** @type {{ mealsPerWeek?: number } } */ (prof).mealsPerWeek) : 0;
      const count = mealCountByProfile.get(pk) || 0;
      if (expected > 0 && count > expected) mealOverflowQty += count - expected;
    });
    const soupOverflowQty = sub.soupExpected === 0 ? soupInCart : Math.max(0, soupInCart - sub.soupExpected);
    const dessertOverflowQty = sub.dessertExpected === 0 ? dessertInCart : Math.max(0, dessertInCart - sub.dessertExpected);

    // Meals without profile key also go to Eenmalig
    let mealUnassignedQty = 0;
    cartItems.forEach((/** @type {TvGuestCartItem} */ item) => {
      if (getItemKind(item) !== 'meal') return;
      if (!getItemProfileKey(item)) mealUnassignedQty += item.quantity || 0;
    });
    const eenmaligQty = mealUnassignedQty + mealOverflowQty + soupOverflowQty + dessertOverflowQty + oneoffQty;

    // Helpers: collect items for profile/soup/dessert slots (oldest-first, capped); excess goes to eenmalig
    /**
     * Meals for one profile, up to progressTotal (add-order = oldest first).
     * @param {string} profileKey
     * @param {number} progressTotal
     * @returns {TvGuestCartItem[]}
     */
    function getProfileMealItems(profileKey, progressTotal) {
      /** @type {TvGuestCartItem[]} */
      const out = [];
      let rendered = 0;
      cartItemsByAddOrder.forEach((item) => {
        if (getItemProfileKey(item) !== profileKey || getItemKind(item) !== 'meal') return;
        for (let q = 0; q < (item.quantity || 0); q++) {
          if (progressTotal > 0 && rendered >= progressTotal) return;
          out.push(item);
          rendered++;
        }
      });
      return out;
    }

    /**
     * Soup items up to cap (oldest first).
     * @param {number} cap
     * @returns {TvGuestCartItem[]}
     */
    function getSoupItems(cap) {
      /** @type {TvGuestCartItem[]} */
      const out = [];
      let rendered = 0;
      cartItemsByAddOrder.forEach((item) => {
        if (getItemProfileKey(item) === 'oneoff' || getItemKind(item) !== 'soup') return;
        for (let q = 0; q < (item.quantity || 0); q++) {
          if (rendered >= cap) return;
          out.push(item);
          rendered++;
        }
      });
      return out;
    }

    /**
     * Dessert items up to cap (oldest first).
     * @param {number} cap
     * @returns {TvGuestCartItem[]}
     */
    function getDessertItems(cap) {
      /** @type {TvGuestCartItem[]} */
      const out = [];
      let rendered = 0;
      cartItemsByAddOrder.forEach((item) => {
        if (getItemProfileKey(item) === 'oneoff' || getItemKind(item) !== 'dessert') return;
        for (let q = 0; q < (item.quantity || 0); q++) {
          if (rendered >= cap) return;
          out.push(item);
          rendered++;
        }
      });
      return out;
    }

    // Assign items to profile/soup/dessert slots (add-order); remaining go to eenmaligItems
    const usedPerProfile = new Map();
    sub.profileKeys.forEach((pk) => usedPerProfile.set(pk, 0));
    /** @type {TvGuestCartItem[]} */
    const eenmaligItems = [];
    cartItemsByAddOrder.forEach((item) => {
      const kind = getItemKind(item);
      const pk = getItemProfileKey(item);
      if (pk === 'oneoff') {
        for (let q = 0; q < (item.quantity || 0); q++) eenmaligItems.push(item);
        return;
      }
      if (kind === 'meal') {
        if (!pk) {
          for (let q = 0; q < (item.quantity || 0); q++) eenmaligItems.push(item);
          return;
        }
        const used = usedPerProfile.get(pk) || 0;
        const profMap = sub.profilesMap.get(pk);
        const expected = (profMap && typeof profMap === 'object' && 'mealsPerWeek' in profMap) ? /** @type {number} */ (/** @type {{ mealsPerWeek?: number } } */ (profMap).mealsPerWeek) : 0;
        let remaining = item.quantity || 0;
        if (expected > 0 && used < expected) {
          const take = Math.min(remaining, expected - used);
          usedPerProfile.set(pk, used + take);
          remaining -= take;
        }
        for (let q = 0; q < remaining; q++) eenmaligItems.push(item);
      } else if (kind === 'soup') {
        if (sub.soupExpected === 0) for (let q = 0; q < (item.quantity || 0); q++) eenmaligItems.push(item);
        else {
          const used = usedPerProfile.get('__soup') || 0;
          let remaining = item.quantity || 0;
          if (used < sub.soupExpected) {
            const take = Math.min(remaining, sub.soupExpected - used);
            usedPerProfile.set('__soup', used + take);
            remaining -= take;
          }
          for (let q = 0; q < remaining; q++) eenmaligItems.push(item);
        }
      } else if (kind === 'dessert') {
        if (sub.dessertExpected === 0) for (let q = 0; q < (item.quantity || 0); q++) eenmaligItems.push(item);
        else {
          const used = usedPerProfile.get('__dessert') || 0;
          let remaining = item.quantity || 0;
          if (used < sub.dessertExpected) {
            const take = Math.min(remaining, sub.dessertExpected - used);
            usedPerProfile.set('__dessert', used + take);
            remaining -= take;
          }
          for (let q = 0; q < remaining; q++) eenmaligItems.push(item);
        }
      }
    });

    // Profile sections (Persoon 1, Persoon 2, …): max 6, matches Liquid guest skeleton; show when subscription expects meals for this profile OR there are meals in cart
    const maxProfileSections = 6;
    sub.profileKeys.forEach((pk, profileIndex) => {
      if (profileIndex >= maxProfileSections) return;
      const section = root.querySelector(`[data-tv-guest-profile][data-tv-guest-profile-index="${profileIndex}"]`);
      if (!section) return;

      const mealCount = mealCountByProfile.get(pk) || 0;
      const p = sub.profilesMap.get(pk);
      const expectedMeals = (p && typeof p === 'object' && 'mealsPerWeek' in p) ? /** @type {number} */ (/** @type {{ mealsPerWeek?: number } } */ (p).mealsPerWeek) : 0;
      const showProfile = expectedMeals > 0 || mealCount > 0;
      setHidden(section, !showProfile);

      const progressTotal = expectedMeals > 0 ? expectedMeals : mealCount;
      const displayedMealCount = expectedMeals > 0 && mealCount > expectedMeals ? expectedMeals : mealCount;

      const nameEl = section.querySelector('[data-tv-guest-profile-name]');
      const countEl = section.querySelector('[data-tv-guest-profile-count]');
      if (nameEl) nameEl.textContent = (p && typeof p === 'object' && 'displayName' in p ? /** @type {{ displayName?: string } } */ (p).displayName : '') || defaultProfileName || pk;
      if (countEl) countEl.textContent = displayedMealCount + '/' + progressTotal;

      const mealItems = getProfileMealItems(pk, /** @type {number} */(progressTotal));
      const cardEls = section.querySelectorAll('[data-tv-guest-profile-meals] [data-tv-guest-card]');
      cardEls.forEach((card, i) => {
        setCardContent(card, mealItems[i] || null, true);
      });

      const slotsContainer = section.querySelector('[data-tv-guest-profile-slots]');
      const slotEls = section.querySelectorAll('[data-tv-guest-profile-slots] [data-tv-guest-slot]');
      const mealRemaining = Math.max(0, progressTotal - mealCount);
      if (slotsContainer) setHidden(slotsContainer, mealRemaining === 0);
      slotEls.forEach((slot, i) => {
        setHidden(slot, i >= mealRemaining);
      });

      const changeEl = section.querySelector('[data-tv-guest-profile-change]');
      if (changeEl) setHidden(changeEl, false);
    });

    // Hide profile section placeholders beyond the number of profiles (e.g. only 2 persons → hide index 2..5)
    for (let pi = sub.profileKeys.length; pi < maxProfileSections; pi++) {
      const section = root.querySelector(`[data-tv-guest-profile][data-tv-guest-profile-index="${pi}"]`);
      if (section) setHidden(section, true);
    }

    // Soup section (Soepen): show when subscription expects soups OR there are soups in cart; fill cards/slots and count (e.g. 0/2, 2/2)
    const soupSection = root.querySelector('[data-tv-guest-soup]');
    if (soupSection) {
      if (sub.soupExpected > 0 || soupInCart > 0) {
        setHidden(soupSection, false);
        const displayedSoup = sub.soupExpected > 0 && soupInCart > sub.soupExpected ? sub.soupExpected : soupInCart;
        const countEl = soupSection.querySelector('[data-tv-guest-soup-count]');
        if (countEl) countEl.textContent = displayedSoup + '/' + sub.soupExpected;

        const soupItems = getSoupItems(sub.soupExpected);
        const cardEls = soupSection.querySelectorAll('[data-tv-guest-soup-meals] [data-tv-guest-card]');
        cardEls.forEach((card, i) => setCardContent(card, soupItems[i] || null, false));

        const soupSlotsContainer = soupSection.querySelector('[data-tv-guest-soup-slots]');
        const slotEls = soupSection.querySelectorAll('[data-tv-guest-soup-slots] [data-tv-guest-slot]');
        const soupRemaining = Math.max(0, sub.soupExpected - displayedSoup);
        if (soupSlotsContainer) setHidden(soupSlotsContainer, soupRemaining === 0);
        slotEls.forEach((slot, i) => { setHidden(slot, i >= soupRemaining); });

        const changeEl = soupSection.querySelector('[data-tv-guest-soup-change]');
        if (changeEl) setHidden(changeEl, false);
      } else {
        setHidden(soupSection, true);
      }
    }

    // Dessert section: show when subscription expects desserts OR there are desserts in cart; fill cards/slots and count (e.g. 0/1, 1/1)
    const dessertSection = root.querySelector('[data-tv-guest-dessert]');
    if (dessertSection) {
      if (sub.dessertExpected > 0 || dessertInCart > 0) {
        setHidden(dessertSection, false);
        const displayedDessert = dessertInCart > sub.dessertExpected ? sub.dessertExpected : dessertInCart;
        const countEl = dessertSection.querySelector('[data-tv-guest-dessert-count]');
        if (countEl) countEl.textContent = displayedDessert + '/' + sub.dessertExpected;

        const dessertItems = getDessertItems(sub.dessertExpected);
        const cardEls = dessertSection.querySelectorAll('[data-tv-guest-dessert-meals] [data-tv-guest-card]');
        cardEls.forEach((card, i) => setCardContent(card, dessertItems[i] || null, false));

        const dessertSlotsContainer = dessertSection.querySelector('[data-tv-guest-dessert-slots]');
        const slotEls = dessertSection.querySelectorAll('[data-tv-guest-dessert-slots] [data-tv-guest-slot]');
        const dessertRemaining = Math.max(0, sub.dessertExpected - displayedDessert);
        if (dessertSlotsContainer) setHidden(dessertSlotsContainer, dessertRemaining === 0);
        slotEls.forEach((slot, i) => { setHidden(slot, i >= dessertRemaining); });

        const changeEl = dessertSection.querySelector('[data-tv-guest-dessert-change]');
        if (changeEl) setHidden(changeEl, false);
      } else {
        setHidden(dessertSection, true);
      }
    }

    // Eenmalig section: one-off/overflow items (unassigned meals, overflow meals/soup/dessert, oneoff); show only when there are items
    const eenmaligSection = root.querySelector('[data-tv-guest-eenmalig]');
    if (eenmaligSection) {
      if (eenmaligQty > 0) {
        setHidden(eenmaligSection, false);
        const countEl = eenmaligSection.querySelector('[data-tv-guest-eenmalig-count]');
        if (countEl) countEl.textContent = String(eenmaligQty);

        const cardEls = eenmaligSection.querySelectorAll('[data-tv-guest-eenmalig-meals] [data-tv-guest-card]');
        cardEls.forEach((card, i) => setCardContent(card, eenmaligItems[i] || null, true));

        const changeEl = eenmaligSection.querySelector('[data-tv-guest-eenmalig-change]');
        if (changeEl) setHidden(changeEl, false);
      } else {
        setHidden(eenmaligSection, true);
      }
    }
  }

  function init() {
    renderGuestContent();
    // Cart update events can fire before sections are morphed, so we schedule a macrotask to read the
    // updated `script#tv-subscription-cart-guest-cart` JSON after the DOM update completes.
    /** @type {number | undefined} */
    let timer;
    const scheduleRender = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        try {
          renderGuestContent();
        } catch {
          // ignore
        }
      }, 0);
    };

    // Re-render when survey payload is updated (e.g. after submit or status check).
    window.addEventListener('ecomm-recommendations-updated', scheduleRender);
    // Re-render after cart updates (add/change/clear).
    document.addEventListener('cart:update', scheduleRender);
    // Re-render on modal open (works even when opened programmatically from the footer).
    window.addEventListener('tv-subscription-cart-modal-opened', scheduleRender);
    // If there are any explicit open buttons in markup, refresh on click too.
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (target instanceof Element && target.closest('[data-tv-subscription-cart-open]')) {
        scheduleRender();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
